import type {
	CampaignBulkDownloadResult,
	CampaignBulkDownloadScope,
	CampaignDownloadPort,
} from '@/domain/campaign-ports'
import { triggerBrowserDownload } from '@/lib/browser-download'
import { apiClient } from '@/services/api.client'
import { fileService } from '@/services/file.service'
import { mapCampaignHttpError } from './campaign-http-error'

/** The wire shape `POST /campaigns/{id}/bulk-download` accepts — mirrors the backend's discriminated `scope` union. */
type CampaignBulkDownloadRequestBody = { scope: 'selected'; fileIds: readonly string[] } | { scope: 'all' }

function bulkDownloadRequestBody(scope: CampaignBulkDownloadScope): CampaignBulkDownloadRequestBody {
	return scope.kind === 'selected' ? { scope: 'selected', fileIds: scope.fileIds } : { scope: 'all' }
}

// HTTP-backed CampaignDownloadPort. Every explicit click requests a fresh
// pre-signed URL and hands it straight to the browser: no URL is cached, stored
// in React Query, or reused, and the body is never fetched as a Blob (which
// would attach Atlas credentials to a URL that is itself the credential).
//
// Only the URL request is observable. Once the browser owns the transfer, a
// failure inside its download manager — or a URL that expired before it was
// used — is not detectable from application code, and nothing here claims
// otherwise. The server's `Content-Disposition` remains the filename authority.
// Capability state is derived elsewhere; this port has no capability operation.

export const campaignDownloadHttpAdapter: CampaignDownloadPort = {
	async downloadIndividual(fileId: string): Promise<void> {
		let url: string
		try {
			url = await fileService.getDownloadUrl(fileId)
		} catch (error) {
			throw mapCampaignHttpError(error, { fileEndpoint: true })
		}
		triggerBrowserDownload(url)
	},

	// One synchronous POST, one generated ZIP, one pre-signed URL, one browser
	// download — never a loop of per-file requests or triggers. A rejection here
	// is the single, well-defined failure signal for the whole request; nothing
	// downstream ever sees a partial result.
	async downloadBulk(campaignId: string, scope: CampaignBulkDownloadScope): Promise<CampaignBulkDownloadResult> {
		let result: CampaignBulkDownloadResult
		try {
			const response = await apiClient.post<CampaignBulkDownloadResult>(
				`/campaigns/${campaignId}/bulk-download`,
				bulkDownloadRequestBody(scope),
			)
			result = response.data
		} catch (error) {
			throw mapCampaignHttpError(error, { fileEndpoint: true })
		}
		triggerBrowserDownload(result.downloadUrl)
		return result
	},
}
